<template>
    <div class="enps-ratings-panel">
        <div v-if="!subject" class="enps-ratings-panel__placeholder">{{ placeholderText }}</div>

        <template v-else>
            <!-- Шапка: имя + крупный средний балл, раскрашенный той же пороговой шкалой, что и
                 бейджи оценок. Среднее у доната скрыто (show-average=false) — одно число не
                 должно висеть в двух сантиметрах от самого себя. -->
            <div class="enps-ratings-panel__header">
                <div>
                    <div class="enps-ratings-panel__name">{{ subject.displayName }}</div>
                    <div class="enps-ratings-panel__subtitle">{{ subtitleText }}</div>
                </div>
                <div class="enps-ratings-panel__average" :class="averageClass">
                    {{ detail.averageRating ?? '—' }}
                    <span class="enps-ratings-panel__average-label">{{ averageLabel }}</span>
                </div>
            </div>

            <VSpinner v-if="isLoading"></VSpinner>

            <template v-else>
                <div class="enps-ratings-panel__columns">
                    <div class="enps-ratings-panel__section">
                        <div class="enps-ratings-panel__section-title">Распределение</div>
                        <EnpsRatingDonut
                            :rating-counts="detail.ratingCounts"
                            :average-rating="detail.averageRating"
                            :show-average="false"
                        ></EnpsRatingDonut>
                    </div>

                    <div class="enps-ratings-panel__section enps-ratings-panel__section_grow">
                        <div class="enps-ratings-panel__section-title">Динамика по месяцам</div>
                        <div v-if="!hasTrendData" class="enps-chart-placeholder" style="height: 180px">
                            Нет оценок за последний год
                        </div>
                        <LineChart
                            v-else
                            :chart-data="trendChartData"
                            :chart-options="trendChartOptions"
                            :styles="{ height: '180px', position: 'relative' }"
                        ></LineChart>
                    </div>
                </div>

                <!-- Каждая оценка по отдельности, в отличие от помесячной средней выше: серия
                     единиц внутри спокойного месяца на средней не видна, а здесь видна сразу.
                     Горизонт свой (последние N), фильтром периода не режется — как и тренд. -->
                <div class="enps-ratings-panel__section">
                    <div class="enps-ratings-panel__section-title">
                        Последние оценки
                        <span class="enps-ratings-panel__count">{{ recentRatings.length }}</span>
                    </div>

                    <div v-if="recentRatings.length === 0" class="enps-chart-placeholder" style="height: 180px">
                        Оценок пока нет
                    </div>
                    <LineChart
                        v-else
                        :chart-data="recentChartData"
                        :chart-options="recentChartOptions"
                        :styles="{ height: '180px', position: 'relative' }"
                    ></LineChart>
                </div>

                <div class="enps-ratings-panel__section">
                    <div class="enps-ratings-panel__section-title">
                        Все оценки
                        <span class="enps-ratings-panel__count">
                            {{ filteredRatings.length
                            }}<template v-if="ratingFilter.length"> из {{ detail.ratings.length }}</template>
                        </span>
                    </div>

                    <!-- Фильтр по баллам: toggle-чипсы, как таблетки месяцев — можно выбрать
                         несколько; пустой выбор = показать все. Чипы только по баллам, которые
                         реально есть в выборке (ряд «2 · 0, 3 · 0…» читался как мусор), а если
                         балл всего один — фильтр не показывается вовсе: делить нечего. -->
                    <div v-if="availableRatingValues.length > 1" class="enps-ratings-panel__rating-filter">
                        <button
                            v-for="value in availableRatingValues"
                            :key="value"
                            type="button"
                            class="enps-ratings-panel__filter-chip"
                            :class="[
                                filterChipClass(value),
                                { 'enps-ratings-panel__filter-chip_active': ratingFilter.includes(value) },
                            ]"
                            @click="toggleRatingFilter(value)"
                        >
                            <span class="enps-ratings-panel__filter-chip-star">★</span>
                            {{ value }}
                            <span class="enps-ratings-panel__filter-chip-count">
                                {{ detail.ratingCounts[value] }}
                            </span>
                        </button>
                    </div>

                    <div v-if="detail.ratings.length === 0" class="enps-ratings-panel__empty">Нет оценок за период</div>

                    <div v-else-if="filteredRatings.length === 0" class="enps-ratings-panel__empty">
                        Нет оценок с выбранными баллами
                    </div>

                    <div v-else class="enps-ratings-panel__ratings">
                        <a
                            v-for="item in filteredRatings"
                            :key="item.requestId"
                            :href="`/request/store/${item.requestId}`"
                            target="_blank"
                            rel="noopener"
                            class="enps-ratings-panel__rating-row"
                            :class="rowClass(item.rating)"
                        >
                            <span class="enps-ratings-panel__rating-head">
                                <span class="enps-ratings-panel__rating-id">Обращение №{{ item.requestId }}</span>
                                <span class="enps-ratings-panel__rating-date">{{ formatClosedAt(item.closedAt) }}</span>
                                <span class="enps-ratings-panel__rating-value" :class="ratingClass(item.rating)">
                                    {{ item.rating }}
                                </span>
                            </span>
                            <span class="enps-ratings-panel__rating-service">
                                {{ item.categoryTitle }} / {{ item.serviceTitle }}
                            </span>
                            <!-- В карточке исполнителя субъект известен из шапки, и второй раз
                                 его имя в каждой строке было бы шумом. В карточке автора здесь
                                 стоит тот, КОГО он оценил — иначе список оценок безадресен. -->
                            <span v-if="item.executorDisplayName" class="enps-ratings-panel__rating-executor">
                                Исполнитель: {{ item.executorDisplayName }}
                            </span>
                            <span
                                class="enps-ratings-panel__rating-comment"
                                :class="{ 'enps-ratings-panel__rating-comment_empty': !item.comment }"
                            >
                                {{ item.comment || 'без комментария' }}
                            </span>
                        </a>
                    </div>
                </div>
            </template>
        </template>
    </div>
</template>
<script>
// Components
import { Line } from 'vue-chartjs/legacy';
import VSpinner from '@/views/components/VProgress/VSpinner.vue';
import EnpsRatingDonut from './EnpsRatingDonut.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import http from '@/js/http';
import ruMonthNames from '@/js/ruMonthNames';
import { SERIES, ratingTier } from '@/js/enpsColors';

const LINE_COLOR = SERIES.averageTrend.main;
const LOW_SAMPLE_COLOR = SERIES.averageTrend.lowSample;

// Цвета точек графика последних оценок — шкала ДИСКРЕТНЫХ бейджей (4-5 / 3 / 1-2), та же, что
// у оценок в списке ниже. Не ratingTier из enpsColors: та шкала для СРЕДНИХ (зелёный строго
// выше 4), и по ней отдельная честная четвёрка красилась бы жёлтым.
const POINT_COLORS = { good: '#00a55a', mid: '#f0a30a', bad: '#f44336' };

// Линия между точками — нейтральный серый: смысл несут точки, а цветная линия сквозь них
// спорила бы с их раскраской.
const RECENT_LINE_COLOR = '#d5d8dc';

const EMPTY_DETAIL = {
    ratings: [],
    ratingCounts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
    averageRating: null,
    ratingsCount: 0,
    monthlyTrend: [],
    recentRatings: [],
};

// Разбор оценок одного человека ПАНЕЛЬЮ справа от списка (бывшая модалка
// VEnpsExecutorCardModal): разбор — последовательный просмотр «кликнул следующего», модалка
// заставляла каждый раз закрывать/открывать и прятала сам список. Содержимое: донат,
// помесячная динамика, график последних оценок и полный список оценок с комментариями —
// каждая строка ведёт в обращение.
//
// Два графика намеренно разные по масштабу: помесячная средняя показывает тренд крупно, а
// график последних оценок — каждую оценку по отдельности. Средняя за месяц прячет серию
// единиц внутри спокойного месяца, вторая её показывает. Оба живут вне фильтра периода
// страницы: у них свои горизонты.
//
// Панель обслуживает ДВА разреза, различающихся пропом role: оценки, полученные исполнителем
// («Исполнители», «Мои оценки»), и оценки, поставленные автором («Авторы»). Отличаются только
// эндпоинт и подписи — арифметика, графики и фильтр по баллам общие, и разводить их в два
// почти одинаковых компонента значило бы чинить каждую правку дважды.
const ROLE_TEXTS = {
    executor: {
        endpoint: 'executors',
        placeholder: 'Выберите исполнителя в рейтинге слева',
        subtitle: (count) => `${count} оценок за период`,
        averageLabel: 'средняя',
    },
    author: {
        endpoint: 'authors',
        placeholder: 'Выберите автора в списке слева',
        subtitle: (count) => `${count} оценок поставлено за период`,
        averageLabel: 'средняя поставленная',
    },
};

@Component({
    components: {
        // Не под родным именем Line: оно совпадает с SVG-элементом <line>, и Vue 2 отказывается
        // регистрировать компонент с зарезервированным именем — график просто не рендерится.
        LineChart: Line,
        VSpinner,
        EnpsRatingDonut,
    },
})
class EnpsRatingsPanel extends Vue {
    // Строка списка слева (accountId + displayName), null — ничего не выбрано.
    @Prop({ type: Object, default: null })
    subject;

    // 'executor' — оценки, ПОЛУЧЕННЫЕ человеком; 'author' — оценки, которые он ПОСТАВИЛ.
    @Prop({ type: String, default: 'executor' })
    role;

    @Prop({ type: String, default: '' })
    period;

    // Отдел исполнителя оценки. У разреза по авторам смысла не имеет, вкладка его не передаёт.
    @Prop({ type: String, default: '' })
    department;
    detail = EMPTY_DETAIL;
    isLoading = false;
    loadSeq = 0;

    // Выбранные баллы фильтра «Все оценки» (пусто — показывать все). Живёт здесь, а не в
    // detail: это состояние просмотра, а не данные с бэкенда.
    ratingFilter = [];

    get texts() {
        return ROLE_TEXTS[this.role] ?? ROLE_TEXTS.executor;
    }

    get placeholderText() {
        return this.texts.placeholder;
    }

    get subtitleText() {
        return this.texts.subtitle(this.detail.ratingsCount);
    }

    get averageLabel() {
        return this.texts.averageLabel;
    }

    get filteredRatings() {
        if (this.ratingFilter.length === 0) {
            return this.detail.ratings;
        }

        return this.detail.ratings.filter((item) => this.ratingFilter.includes(item.rating));
    }

    // Баллы, которые реально встречаются в выборке, по возрастанию — только из них строятся
    // чипы фильтра.
    get availableRatingValues() {
        return [1, 2, 3, 4, 5].filter((value) => this.detail.ratingCounts[value] > 0);
    }

    // Средняя в шапке красится общей шкалой СРЕДНИХ (ratingTier: >4 / 3-4 / <3), а не шкалой
    // дискретных бейджей — средняя ровно 4.0 ещё не зелёная, в отличие от оценки ★4.
    get averageClass() {
        const tier = ratingTier(this.detail.averageRating);

        return tier ? `enps-ratings-panel__average_${tier}` : '';
    }

    get hasTrendData() {
        return (this.detail.monthlyTrend || []).some((row) => row.ratingsCount > 0);
    }

    get trendChartData() {
        const trend = this.detail.monthlyTrend || [];

        return {
            labels: trend.map((row) => this.monthLabel(row.month)),
            datasets: [
                {
                    label: 'Средняя оценка',
                    // Без клипа по chartArea: точки на краях шкалы (0 и 5) иначе срезаются
                    // наполовину границей области графика.
                    clip: false,
                    // Месяц без оценок — 0, линия непрерывна и опускается к нулю (решение
                    // владельца продукта). Подсказка на такой точке говорит «Нет оценок»,
                    // отличая её от настоящей нулевой средней.
                    data: trend.map((row) => row.averageRating ?? 0),
                    borderColor: LINE_COLOR,
                    backgroundColor: LINE_COLOR,
                    // Месяц с 1-2 оценками помечается бледной точкой — тем же порогом, что и
                    // везде на дашборде: среднее по двум оценкам это не тренд.
                    pointBackgroundColor: trend.map((row) => (row.isLowSample ? LOW_SAMPLE_COLOR : LINE_COLOR)),
                    pointRadius: 4,
                    tension: 0.3,
                },
            ],
        };
    }

    // Бэкенд отдаёт последние оценки уже в хронологическом порядке (старые слева) — ось
    // разворачивать не нужно, см. recentRatings() в EnpsDashboardService.
    get recentRatings() {
        return this.detail.recentRatings || [];
    }

    get recentChartData() {
        const ratings = this.recentRatings;

        return {
            labels: ratings.map((item) => this.formatClosedAt(item.closedAt)),
            datasets: [
                {
                    label: 'Оценка',
                    // Без клипа по chartArea: точки на краях шкалы (1 и 5) иначе срезаются
                    // наполовину границей области графика.
                    clip: false,
                    data: ratings.map((item) => item.rating),
                    borderColor: RECENT_LINE_COLOR,
                    backgroundColor: RECENT_LINE_COLOR,
                    pointBackgroundColor: ratings.map((item) => this.pointColor(item.rating)),
                    pointBorderColor: ratings.map((item) => this.pointColor(item.rating)),
                    pointRadius: 4,
                    // tension: 0 намеренно. Оценки — отдельные события, а не непрерывная
                    // величина; сглаженная кривая обещала бы промежуточные значения, которых
                    // не существует (в отличие от помесячной средней выше).
                    tension: 0,
                },
            ],
        };
    }

    get recentChartOptions() {
        const ratings = this.recentRatings;

        return {
            responsive: true,
            maintainAspectRatio: false,
            // Место под точки на краях шкалы (clip: false у датасета).
            layout: { padding: { top: 8 } },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label(context) {
                            const item = ratings[context.dataIndex] || {};

                            return `Обращение №${item.requestId} · ${item.rating}`;
                        },
                    },
                },
            },
            scales: {
                // Шкала 1..5, а не 0..5 как у средней: ноль — невозможная оценка, и место под
                // него сплющивало бы реальный разброс.
                y: { min: 1, max: 5, ticks: { stepSize: 1 } },
                // Даты под осью. Все 30 не поместятся, поэтому autoSkip (включён в Chart.js по
                // умолчанию) прореживает их до того, что влезает, а maxRotation: 0 запрещает
                // разворачивать подписи по диагонали — косой текст читается хуже, чем более
                // редкий прямой. Подпись остаётся полной датой (не дд.ММ): она же идёт
                // заголовком подсказки, где год терять нельзя — 30 оценок могут пересечь его.
                x: { ticks: { maxRotation: 0, autoSkipPadding: 16 } },
            },
        };
    }

    pointColor(rating) {
        if (rating >= 4) {
            return POINT_COLORS.good;
        }

        return rating === 3 ? POINT_COLORS.mid : POINT_COLORS.bad;
    }

    get trendChartOptions() {
        const trend = this.detail.monthlyTrend || [];

        return {
            responsive: true,
            maintainAspectRatio: false,
            // Место под точки на краях шкалы (clip: false у датасета): легенда скрыта, и без
            // отступа верхней точке некуда рисоваться.
            layout: { padding: { top: 8 } },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label(context) {
                            const row = trend[context.dataIndex] || {};

                            if (!row.ratingsCount) {
                                return 'Нет оценок';
                            }

                            return `${row.averageRating} · ${row.ratingsCount} оценок`;
                        },
                    },
                },
            },
            scales: {
                y: { min: 0, max: 5 },
            },
        };
    }

    monthLabel(month) {
        const [year, monthNumber] = month.split('-');

        return `${ruMonthNames[Number(monthNumber) - 1]} ${year.slice(2)}`;
    }

    ratingClass(rating) {
        if (rating >= 4) {
            return 'enps-ratings-panel__rating-value_good';
        }

        return rating === 3 ? 'enps-ratings-panel__rating-value_mid' : 'enps-ratings-panel__rating-value_bad';
    }

    // Та же пороговая раскраска (4-5 / 3 / 1-2), что у бейджа оценки — для цветной кромки
    // строки и чипсов фильтра.
    rowClass(rating) {
        if (rating >= 4) {
            return 'enps-ratings-panel__rating-row_good';
        }

        return rating === 3 ? 'enps-ratings-panel__rating-row_mid' : 'enps-ratings-panel__rating-row_bad';
    }

    filterChipClass(rating) {
        if (rating >= 4) {
            return 'enps-ratings-panel__filter-chip_good';
        }

        return rating === 3 ? 'enps-ratings-panel__filter-chip_mid' : 'enps-ratings-panel__filter-chip_bad';
    }

    toggleRatingFilter(value) {
        this.ratingFilter = this.ratingFilter.includes(value)
            ? this.ratingFilter.filter((rating) => rating !== value)
            : this.ratingFilter.concat(value);
    }

    formatClosedAt(value) {
        if (!value) {
            return '';
        }

        const [datePart] = value.split(' ');
        const [year, month, day] = datePart.split('-');

        return `${day}.${month}.${year}`;
    }

    @Watch('subject')
    @Watch('role')
    @Watch('period')
    @Watch('department')
    onScopeChange() {
        this.load();
    }

    load() {
        // Фильтр баллов относится к конкретному набору оценок — при смене человека,
        // периода или отдела он сбрасывается, чтобы не показывать «пусто» из-за фильтра,
        // выставленного под другого. Сброс detail до ответа — по той же причине:
        // шапка панели рендерится и во время загрузки, и без сброса она показывала бы имя
        // нового человека с цифрами предыдущего.
        this.ratingFilter = [];
        this.detail = EMPTY_DETAIL;

        // Инкремент даже в ветке без выбранного: снятие выбора тоже должно инвалидировать
        // запрос, который ещё в полёте, иначе его поздний ответ запишется в пустую панель.
        // isLoading гасится здесь же — finally того запроса из-за инкремента его не тронет.
        const seq = ++this.loadSeq;

        if (!this.subject) {
            this.isLoading = false;
            return;
        }

        // Без клиентского кэша (убран решением владельца продукта: он переживал только сессию
        // вкладки, зато отдавал устаревшие цифры после новых оценок), поэтому от гонок при
        // быстрых кликах по списку защищает счётчик: применяется только последний запрос.
        this.isLoading = true;

        http.get(`/enps/dashboard/${this.texts.endpoint}/${this.subject.accountId}`, {
            params: { period: this.period, department: this.department },
        })
            .then(({ data }) => {
                if (seq !== this.loadSeq) {
                    return;
                }

                this.detail = data;
            })
            .catch(() => {
                if (seq !== this.loadSeq) {
                    return;
                }

                this.detail = EMPTY_DETAIL;
            })
            .finally(() => {
                if (seq === this.loadSeq) {
                    this.isLoading = false;
                }
            });
    }

    created() {
        this.load();
    }
}

export default EnpsRatingsPanel;
</script>
<style lang="scss">
.enps-ratings-panel {
    flex: 1;
    min-width: 0;
    padding: 20px;
    border-radius: 8px;
    background-color: var(--color-white);
    box-shadow: 0px 1px 10px rgba(88, 88, 88, 0.15);
    align-self: flex-start;

    &__placeholder {
        padding: 40px 0;
        color: #a0a0a0;
        text-align: center;
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 15px;
        margin-bottom: 15px;
        padding-bottom: 15px;
        border-bottom: 1px solid #e4eaef;
    }

    &__name {
        font-size: 1.125rem;
        font-weight: 600;
    }

    &__subtitle {
        margin-top: 2px;
        color: #767676;
        font-size: 0.8125rem;
    }

    &__average {
        font-size: 1.75rem;
        font-weight: 600;
        line-height: 1.1;
        text-align: right;
        white-space: nowrap;

        &_good {
            color: #00a55a;
        }

        &_mid {
            color: #f0a30a;
        }

        &_bad {
            color: #f44336;
        }
    }

    &__average-label {
        display: block;
        color: #a0a0a0;
        font-size: 0.75rem;
        font-weight: 400;
    }

    // Донат и динамика — в одну строку: столбиком карточка вытягивалась на два экрана, а обе
    // секции невысокие. На узкой панели колонки переносятся.
    &__columns {
        display: flex;
        gap: 20px;
        flex-wrap: wrap;
    }

    &__section {
        margin-bottom: 20px;

        &_grow {
            flex: 1;
            min-width: 260px;
        }

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__section-title {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        font-weight: 600;
        gap: 8px;
    }

    &__count {
        padding: 1px 8px;
        border-radius: 10px;
        background-color: #eef2f5;
        color: #767676;
        font-size: 0.75rem;
        font-weight: 400;
    }

    &__empty {
        padding: 14px 0;
        color: #a0a0a0;
        text-align: center;
    }

    &__rating-filter {
        display: flex;
        gap: 6px;
        margin-bottom: 10px;
        flex-wrap: wrap;
    }

    // Чип «★ 5 [2]»: звезда говорит «это балл», пузырёк — «столько оценок». Без рамки, как
    // остальные чипы дашборда; звезда окрашена шкалой балла, активный чип заливается ею целиком.
    &__filter-chip {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 3px 9px 3px 11px;
        border: none;
        border-radius: 12px;
        background-color: #f2f2f2;
        color: #3c4858;
        font-size: 0.8125rem;
        line-height: 1.5;
        cursor: pointer;
        transition: background-color 0.15s, color 0.15s;

        &:hover {
            background-color: #e8e8e8;
        }
    }

    &__filter-chip-star {
        font-size: 0.6875rem;
    }

    &__filter-chip-count {
        min-width: 18px;
        padding: 0 5px;
        border-radius: 9px;
        background-color: rgba(60, 72, 88, 0.08);
        color: #767676;
        font-size: 0.6875rem;
        line-height: 1.6;
        text-align: center;
    }

    &__filter-chip_good .enps-ratings-panel__filter-chip-star {
        color: #00a55a;
    }

    &__filter-chip_mid .enps-ratings-panel__filter-chip-star {
        color: #f0a30a;
    }

    &__filter-chip_bad .enps-ratings-panel__filter-chip-star {
        color: #f44336;
    }

    // Активные чипы — семантические ПОЛУТОНА (светлая заливка + тёмный тон того же цвета):
    // сплошные зелёно-красные пятна спорили с акцентом страницы, а полутон читается так же.
    &__filter-chip_good#{&}__filter-chip_active {
        background-color: #e3f3ea;
        color: #078552;

        &:hover {
            background-color: #e3f3ea;
        }
    }

    &__filter-chip_mid#{&}__filter-chip_active {
        background-color: #fdf3d9;
        color: #a97806;

        &:hover {
            background-color: #fdf3d9;
        }
    }

    &__filter-chip_bad#{&}__filter-chip_active {
        background-color: #fde7e4;
        color: #c62828;

        &:hover {
            background-color: #fde7e4;
        }
    }

    &__filter-chip_active {
        font-weight: 600;

        .enps-ratings-panel__filter-chip-count {
            background-color: rgba(60, 72, 88, 0.1);
            color: inherit;
        }
    }

    // Список не ограничен по числу строк (бэкенд отдаёт все оценки за период), поэтому у него
    // своя прокрутка — иначе донат и график уезжали бы за экран у исполнителя с сотней оценок.
    &__ratings {
        max-height: 360px;
        overflow-y: auto;
        border: 1px solid #e4eaef;
        border-radius: 6px;

        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-thumb {
            border-radius: 3px;
            background-color: #d5dde4;
        }
    }

    &__rating-row {
        display: block;
        padding: 8px 12px;
        // Цветная кромка по баллу (та же шкала, что у бейджа) — при прокрутке длинного списка
        // проблемные строки видны боковым зрением, без чтения цифр.
        border-left: 3px solid transparent;
        border-bottom: 1px solid #e4eaef;
        color: inherit;
        text-decoration: none;
        cursor: pointer;
        transition: background-color 0.15s;

        &_good {
            border-left-color: #00a55a;
        }

        &_mid {
            border-left-color: #f0a30a;
        }

        &_bad {
            border-left-color: #f44336;
        }

        &:last-child {
            border-bottom: none;
        }

        &:hover {
            background-color: #faf6f2;

            .enps-ratings-panel__rating-id {
                text-decoration: underline;
            }
        }
    }

    &__rating-head {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__rating-id {
        // Тёмный жжёный оттенок акцента (ACCENT_DEEP из enpsColors.js): это ссылка, а не
        // оценка; светлый корп-оранж как цвет текста плохо читается.
        color: #c05a1f;
        font-weight: 600;
    }

    &__rating-date {
        flex: 1;
        color: #a0a0a0;
        font-size: 0.8125rem;
    }

    &__rating-value {
        min-width: 24px;
        padding: 1px 7px;
        border-radius: 10px;
        color: #fff;
        font-size: 0.8125rem;
        font-weight: 600;
        text-align: center;

        &_good {
            background-color: #00a55a;
        }

        &_mid {
            background-color: #f0a30a;
        }

        &_bad {
            background-color: #f44336;
        }
    }

    &__rating-service {
        display: block;
        margin-top: 2px;
        color: #767676;
        font-size: 0.8125rem;
    }

    // Кого оценил автор — только в разрезе авторов. Тот же приглушённый регистр, что у
    // категории/услуги: это подпись к строке, а не её заголовок.
    &__rating-executor {
        display: block;
        margin-top: 2px;
        color: #767676;
        font-size: 0.8125rem;
    }

    &__rating-comment {
        display: block;
        margin-top: 4px;
        font-size: 0.875rem;

        &_empty {
            color: #a0a0a0;
            font-style: italic;
        }
    }
}
</style>
